/* eslint-disable no-unused-vars */
import { get } from "lodash";
import React, { Suspense, forwardRef, lazy, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { CONTROL_TYPE } from "@lhd/components/Formik";
import { ERRORS_MESSAGE, REGEX, TableKey, WORKER } from "@lhd/constants";
import { toHalfWidth } from "@lhd/features/Salary/SalarySheets/components/exportPDF/utility";
import { Worker } from "@lhd/types";
import {
  convertCharterFullWidth,
  formatCurrency,
  formatDateTimeJP,
} from "@lhd/utility";
import { ColDef } from "ag-grid-community";
import { ColDefCustom } from "types/tableWithForm.type";
import { BANK_TYPE_OPTIONS } from "@lhd/constants";
import { registrationCodeTableColumnDefs } from "./components/ProfileSection/RegistrationCodeTable/utility";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import { RULE_TYPE, TYPE_INPUT } from "@lhd/components/TableForm/types";

// eslint-disable-next-line react/display-name
export default forwardRef((props: any, _ref) => {
  const inputRef: any = useRef<HTMLInputElement>(null);
  const auth = useSelector(user);
  const WORKER = auth?.workingTypeOptions || {};
  const WORKER_TYPE_OPTIONS = Object.keys(WORKER)?.map((i) => ({
    value: i,
    label: WORKER[i],
  }));
  const onBoxChanged = (input: any) => {
    if (input.target.value === "") {
      props.parentFilterInstance((instance: any) => {
        instance.onFloatingFilterChanged(null, null);
      });
      return;
    }

    props.parentFilterInstance((instance: any) => {
      instance.onFloatingFilterChanged("greaterThan", input.target.value);
    });
  };

  return (
    <div className="floatingFilter-select">
      <select ref={inputRef} onInput={onBoxChanged}>
        <option value="clear">すべて</option>
        {WORKER_TYPE_OPTIONS?.map((i) => (
          <option value={i?.value} key={i?.value}>
            {i?.label}
          </option>
        ))}
      </select>
    </div>
  );
});

export const columns = () => {
  return [
    {
      headerName: "",
      field: "index",
      cellRenderer: (params: any) => {
        return params.rowIndex + 1;
      },
      minWidth: 30,
      maxWidth: 30,
      filter: false,
      sortable: false,
      pinned: "left",
    },
    {
      headerName: "",
      field: "person.isChecked",
      minWidth: 50,
      width: 50,
      pinned: "left",
      cellStyle: { textAlign: "center", justifyContent: "start" },
      filter: false,
      sortable: false,
    },
    {
      headerName: "姓",
      field: "first_name",
      minWidth: 150,
      pinned: "left",
      cellStyle: { textAlign: "center", justifyContent: "start" },
    },
    {
      headerName: "名",
      field: "last_name",
      minWidth: 150,
      pinned: "left",
      cellStyle: { textAlign: "center", justifyContent: "start" },
    },
    {
      headerName: "メール",
      field: "user.email",
      minWidth: 230,
    },
    {
      headerName: "カナ",
      field: "full_name_kana",
      minWidth: 230,
    },
    {
      headerName: "電話番号",
      field: "tel",
      minWidth: 230,
    },
    {
      headerName: "住所",
      field: "address",
      minWidth: 230,
    },
    {
      headerName: "誕生日",
      field: "birthday",
      minWidth: 150,
      cellRenderer: ({ value }: { value: string }) => (
        <div>{value && formatDateTimeJP(value)}</div>
      ),
    },
    {
      headerName: "契約種別",
      field: "working_type",
      minWidth: 140,
      cellRenderer: (e: any) => <div>{get(WORKER, e.value, "")}</div>,
    },
    {
      headerName: "スタッフ番号",
      field: "worker_no",
      minWidth: 140,
    },
    {
      headerName: "商号",
      field: "trade_name",
      minWidth: 140,
    },
    {
      headerName: "銀行名",
      field: "bank_name",
      minWidth: 140,
    },
    {
      headerName: "支店名",
      field: "bank_branch",
      minWidth: 140,
    },
    {
      headerName: "口座種別",
      field: "bank_type",
      type: CONTROL_TYPE.SELECT,
      maxWidth: 100,
      minWidth: 100,
      options: BANK_TYPE_OPTIONS,
    },
    {
      headerName: "口座番号",
      field: "bank_number",
      minWidth: 140,
    },
    {
      headerName: "マイナンバー",
      field: "my_number",
      minWidth: 140,
    },
    {
      headerName: "雇用保険被保険者番号",
      field: "unemployment_insurance_number",
      minWidth: 200,
    },
    {
      headerName: "社会保険証番号",
      field: "insurance_card_number",
      minWidth: 150,
    },
    {
      headerName: "名前",
      field: "emergency_name",
      minWidth: 140,
    },
    {
      headerName: "続柄",
      field: "emergency_relation",
      minWidth: 140,
    },
    {
      headerName: "住所",
      field: "emergency_address",
      minWidth: 140,
    },
    {
      headerName: "連絡先",
      field: "emergency_information",
      minWidth: 140,
    },
    {
      headerName: "入社日",
      field: "join_date",
      minWidth: 140,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>{data.join_date && formatDateTimeJP(data.join_date)}</div>
      ),
    },
    {
      headerName: "退社日",
      field: "retired_date",
      minWidth: 140,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>{data.retired_date && formatDateTimeJP(data.retired_date)}</div>
      ),
    },
    {
      headerName: "標準報酬月額",
      field: "base_salary",

      minWidth: 140,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>{data.base_salary && formatCurrency(data.base_salary)}</div>
      ),
    },
    {
      headerName: "保険適用都道府県（※事業所所在地）",
      field: "prefecture",
      minWidth: 160,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>{data.prefecture?.name || ""}</div>
      ),
    },
    {
      headerName: "健康保険資格取得年月日",
      field: "health_insurance_get_date",
      minWidth: 200,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>
          {data.health_insurance_get_date &&
            formatDateTimeJP(data.health_insurance_get_date)}
        </div>
      ),
    },
    {
      headerName: "健康保険資格喪失年月日",
      field: "health_insurance_lost_date",
      minWidth: 200,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>
          {data.health_insurance_lost_date &&
            formatDateTimeJP(data.health_insurance_lost_date)}
        </div>
      ),
    },
    {
      headerName: "雇用保険資格取得年月日",
      field: "employment_insurance_get_date",
      minWidth: 200,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>
          {data.employment_insurance_get_date &&
            formatDateTimeJP(data.employment_insurance_get_date)}
        </div>
      ),
    },
    {
      headerName: "雇用保険資格喪失年月日",
      field: "employment_insurance_lost_date",
      minWidth: 200,
      cellRenderer: ({ data }: { data: Worker }) => (
        <div>
          {data.employment_insurance_lost_date &&
            formatDateTimeJP(data.employment_insurance_lost_date)}
        </div>
      ),
    },
  ];
};

export const FILTER_PARAMS = {
  full_name: "q[full_name_cont]",
  "user.email": "q[person_user_email_cont]",
  tel: "q[tel_cont]",
  address: "q[address_cont]",
  birthday: "filter_birthday",
  full_name_kana: "q[full_name_kana_cont]",
  first_name: "q[first_name_cont]",
  last_name: "q[last_name_cont]",
  first_name_kana: "q[first_name_kana_cont]",
  last_name_kana: "q[last_name_kana_cont]",
  working_type: "q[working_type_eq]",
  worker_no: "q[worker_no_cont]",
  trade_name: "q[trade_name_cont]",
  bank_name: "q[bank_name_cont]",
  bank_branch: "q[bank_branch_cont]",
  bank_type: "q[bank_type_cont]",
  bank_number: "q[bank_number_cont]",
  my_number: "q[my_number_cont]",
  unemployment_insurance_number: "q[unemployment_insurance_number_cont]",
  insurance_card_number: "q[insurance_card_number_cont]",
  emergency_name: "q[emergency_name_cont]",
  emergency_relation: "q[emergency_relation_cont]",
  emergency_address: "q[emergency_address_cont]",
  emergency_information: "q[emergency_information_cont]",
  join_date: "filter_join_date",
  retired_date: "filter_retired_date",
  base_salary: "q[base_salary_eq]",
  prefecture: "q[prefecture_name_cont]",
  health_insurance_get_date: "filter_health_insurance_get_date",
  health_insurance_lost_date: "filter_health_insurance_lost_date",
  employment_insurance_get_date: "filter_employment_insurance_get_date",
  employment_insurance_lost_date: "filter_employment_insurance_lost_date",
  chart_organizations: "filter_organization_name",
  chart_positions: "filter_position_name",
  chart_org_position_texts: "filter_by_org_or_position",
};

export const columnLoginInfoDefs: ColDefCustom[] = [
  {
    headerName: "ログインID（メールアドレス）",
    field: "email",
    type: CONTROL_TYPE.INPUT,
    minWidth: 400,
    isRequired: true,
    editInfo: {
      type: TYPE_INPUT.EMAIL,
      rules: [
        {
          type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY,
        },
      ],
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    timeRangeLock: true,
    lessThanField: "end_date",
    minWidth: 150,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    disabledReCordPast: true,
    greaterThanField: "start_date",
    minWidth: 150,
  },
  {
    headerName: "システム日", // System date (read-only)
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    minWidth: 150,
    disabled: true,
  },
  {
    headerName: "更新者", // Updated by (read-only)
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    minWidth: 150,
    disabled: true,
  },
];

/** ダミーデータ for `user_logins` */

//=========
export const nameHistoryColumnDefs: ColDefCustom[] = [
  {
    headerName: "姓",
    field: "firstname",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
  },
  {
    headerName: "名",
    field: "lastname",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
  },
  {
    headerName: "セイ(全角)",
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
    field: "firstname_kana",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
  },
  {
    headerName: "メイ(全角)",
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
    field: "lastname_kana",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
  },
  {
    headerName: "開始日",
    editInfo: {
      type: TYPE_INPUT.DATE,
      rules: [{ type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY }],
    },
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    timeRangeLock: true,
    lessThanField: "end_date",
    endGreaterStart: true,
    isRequired: true,
    minWidth: 150,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    disabledReCordPast: true,
    grayRecordPast: true,
    minWidth: 150,
    disabled: true,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    minWidth: 150,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 200,
  },
];

/** ダミーデータ */

//===========================

export const genderColumnDefs: ColDefCustom[] = [
  {
    headerName: "性別（体）",
    field: "biological_sex",
    type: CONTROL_TYPE.INPUT,
    minWidth: 160,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "性別（心）",
    field: "gender_identity",
    type: CONTROL_TYPE.INPUT,
    minWidth: 160,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 150,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    disabledReCordPast: false,
    grayRecordPast: true,
    minWidth: 150,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 150,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    disabled: true,
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
  },
];

//===========================
export const addressNowColumnDefs: ColDefCustom[] = [
  {
    headerName: "住所種類",
    field: "address_type",
    type: CONTROL_TYPE.CHECKBOX,
    minWidth: 460,
    options: [
      { label: "現住所", value: "is_current_address" },
      { label: "住民票", value: "is_resident_record" },
      { label: "書類送付先", value: "is_document_submission" },
      { label: "支払計算書記載", value: "is_payment_calculation" },
    ],
  },
  {
    headerName: "〒",
    field: "zip_code",
    fieldAddress: "address",
    fieldPrefecture: "prefecture_id",
    type: CONTROL_TYPE.ZIP_CODE,
    minWidth: 120,
    editInfo: {
      type: TYPE_INPUT.ZIPCODE,
      fieldAddress: "address",
      fieldPrefecture: "prefecture.name",
    },
  },
  {
    headerName: "都道府県",
    field: "prefecture_id",
    type: CONTROL_TYPE.PREFECTURE,
    minWidth: 260,
    editInfo: {
      type: TYPE_INPUT.PREFECTURE,
    },
  },
  {
    headerName: "住所（市区町村・番地）",
    field: "address",
    type: CONTROL_TYPE.INPUT,
    minWidth: 260,
    editInfo: {
      type: TYPE_INPUT.ADDRESS,
    },
  },
  {
    headerName: "アパート・マンション",
    field: "apartment",
    type: CONTROL_TYPE.INPUT,
    minWidth: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 170,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    disabledReCordPast: false,
    grayRecordPast: true,
    endGreaterStart: true,
    minWidth: 170,
  },
  {
    headerName: "システム日",
    field: "created_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 170,
  },
];

export const residentAddrColumnDefs: ColDefCustom[] = [
  {
    headerName: "〒",
    field: "zip_code",
    fieldAddress: "address",
    type: CONTROL_TYPE.ZIP_CODE,
    minWidth: 120,
  },
  {
    headerName: "住民票住所",
    field: "address",
    type: CONTROL_TYPE.INPUT,
    minWidth: 260,
  },
  {
    headerName: "アパート・マンション",
    field: "apartment",
    type: CONTROL_TYPE.INPUT,
    minWidth: 200,
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 170,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    disabledReCordPast: false,
    grayRecordPast: true,
    minWidth: 170,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    disabled: true,
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
  },
];

//
export const phoneColumnDefs: ColDefCustom[] = [
  {
    headerName: "電話番号",
    field: "phone_number",
    type: CONTROL_TYPE.PHONE_NUMBER,
    minWidth: 200,
    editInfo: {
      type: TYPE_INPUT.PHONE_NUMBER,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 170,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    disabledReCordPast: false,
    grayRecordPast: true,
    minWidth: 170,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    disabled: true,
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
  },
];

export const mobileColumnDefs: ColDefCustom[] = [
  {
    headerName: "携帯番号",
    field: "phone_number",
    type: CONTROL_TYPE.PHONE_NUMBER,
    minWidth: 200,
    editInfo: {
      type: TYPE_INPUT.PHONE_NUMBER,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 170,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    grayRecordPast: true,
    disabledReCordPast: false,
    minWidth: 170,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    disabled: true,
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
  },
];

//=======
export const shopNameColumnDefs: ColDefCustom[] = [
  {
    headerName: "屋号",
    field: "store_name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 220,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    minWidth: 170,
    timeRangeLock: true,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    grayRecordPast: true,
    disabledReCordPast: false,
    minWidth: 170,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    disabled: true,
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
  },
];

//銀行口座テーブル ( Bank Accounts )
export enum BANK_ACCOUNT_TYPE {
  SALARY = "is_salary_account",
  PAYMENT = "is_payment_account",
}
export const bankAccountColumnDefs: ColDefCustom[] = [
  {
    headerName: "口座選択",
    field: "account_bank_type",
    type: CONTROL_TYPE.CHECKBOX,
    minWidth: 230,
    options: [
      { label: "給与", value: BANK_ACCOUNT_TYPE.SALARY },
      { label: "支払計算書", value: BANK_ACCOUNT_TYPE.PAYMENT },
    ],
  },
  {
    headerName: "銀行名",
    field: "bank_name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "支店名",
    field: "bank_branch",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "口座種別",
    field: "account_type",
    type: CONTROL_TYPE.SELECT,
    minWidth: 150,
    options: BANK_TYPE_OPTIONS,
    editInfo: {
      type: TYPE_INPUT.SELECT,
    },
  },
  {
    headerName: "口座番号",
    field: "account_number",
    type: CONTROL_TYPE.INPUT_POSITIVE_NUMBER,
    minWidth: 180,
    editInfo: {
      type: TYPE_INPUT.INPUT_POSITIVE_NUMBER,
    },
  },
  {
    headerName: "口座名義",
    field: "account_holder_name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "備考",
    field: "note",
    type: CONTROL_TYPE.INPUT,
    minWidth: 220,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  // {
  //   headerName: "枝番号",
  //   field: "branch_code",
  //   type: CONTROL_TYPE.INPUT,
  //   minWidth: 170,
  // },
  {
    headerName: "開始日",
    field: "account_open_date",
    type: CONTROL_TYPE.BANK_ACCOUNT_DATE,
    minWidth: 170,
    // lessThanField: "account_close_date",
    // endGreaterStart: true,
  },
  {
    headerName: "終了日",
    field: "account_close_date",
    type: CONTROL_TYPE.BANK_ACCOUNT_DATE,
    minWidth: 170,
    disabledReCordPast: false,
    grayRecordPast: true,
    disabled: true,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 170,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 170,
  },
];

//====緊急連絡先 – 基本情報テーブル ( Emergency Contacts – Basic )
export const emergencyBasicColumnDefs: ColDefCustom[] = [
  {
    headerName: "姓",
    field: "first_name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "名",
    field: "last_name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "セイ(全角)",
    field: "first_name_kana",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "メイ(全角)",
    field: "last_name_kana",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "続柄",
    field: "relationship",
    type: CONTROL_TYPE.INPUT,
    minWidth: 150,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "電話番号",
    field: "phone_number",
    type: CONTROL_TYPE.PHONE_NUMBER,
    minWidth: 150,
    editInfo: {
      type: TYPE_INPUT.PHONE_NUMBER,
    },
  },
  {
    headerName: "携帯番号",
    field: "mobile_number",
    type: CONTROL_TYPE.PHONE_NUMBER,
    minWidth: 150,
    editInfo: {
      type: TYPE_INPUT.PHONE_NUMBER,
    },
  },
  {
    headerName: "メールアドレス",
    field: "email",
    type: CONTROL_TYPE.INPUT,
    minWidth: 160,
    editInfo: {
      type: TYPE_INPUT.EMAIL,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    minWidth: 150,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    disabledReCordPast: false,
    grayRecordPast: true,
    minWidth: 150,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 150,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 150,
  },
];

//緊急連絡先 – 住所テーブル ( Emergency Contacts – Address )
export const emergencyAddrColumnDefs: ColDefCustom[] = [
  {
    headerName: "〒",
    field: "zip_code",
    fieldAddress: "address",
    fieldPrefecture: "prefecture_id",
    type: CONTROL_TYPE.ZIP_CODE,
    minWidth: 120,
    editInfo: {
      type: TYPE_INPUT.ZIPCODE,
      fieldAddress: "address",
      fieldPrefecture: "prefecture.name",
    },
  },
  {
    headerName: "都道府県",
    field: "prefecture_id",
    type: CONTROL_TYPE.PREFECTURE,
    minWidth: 260,
    editInfo: {
      type: TYPE_INPUT.PREFECTURE,
    },
  },
  {
    headerName: "住所（市区町村・番地）",
    field: "address",
    type: CONTROL_TYPE.INPUT,
    minWidth: 260,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "アパート・マンション",
    field: "apartment",
    type: CONTROL_TYPE.INPUT,
    minWidth: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    lessThanField: "end_date",
    endGreaterStart: true,
    minWidth: 150,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    disabledReCordPast: false,
    grayRecordPast: true,
    endGreaterStart: true,
    minWidth: 150,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    disabled: true,
    type: CONTROL_TYPE.DATE,
    minWidth: 150,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 150,
  },
];

//=
/* Columns */
export const qualificationsColumnDefs: ColDefCustom[] = [
  {
    headerName: "年",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 130,
    editInfo: {
      type: TYPE_INPUT.DATE_MONTH,
    },
  },

  {
    headerName: "資格・免許",
    field: "name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "備考",
    field: "comment",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "添付書類",
    field: "worker_attachments",
    type: CONTROL_TYPE.FILE,
    minWidth: 170,
  },
];

//学歴・職歴テーブル (Academic / Career history)
export const educationWorkColumnDefs: ColDefCustom[] = [
  {
    headerName: "年",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 130,
    endGreaterStart: true,
    lessThanField: "end_date",
    editInfo: {
      type: TYPE_INPUT.DATE_MONTH,
    },
  },
  {
    headerName: "～",
    field: "SEPARATELY",
    type: CONTROL_TYPE.SEPARATELY,
    width: 30,
  },
  {
    headerName: "年",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 130,
    endGreaterStart: true,
    greaterThanField: "start_date",
    editInfo: {
      type: TYPE_INPUT.DATE_MONTH,
    },
  },
  {
    headerName: "期間",
    field: "period",
    type: CONTROL_TYPE.PERIOD,
    field_start: "start_date",
    field_end: "end_date",
    width: 90,
    unit: "年",
    typeTime: "year",
  },
  {
    headerName: "学歴・職歴",
    field: "name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "備考",
    field: "comment",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "添付書類",
    field: "worker_attachments",
    type: CONTROL_TYPE.FILE,
    width: 140,
  },
];

export const jobDetailsColumnDefs: ColDefCustom[] = [
  {
    headerName: "年",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 130,
    endGreaterStart: true,
    lessThanField: "end_date",
    editInfo: {
      type: TYPE_INPUT.DATE_MONTH,
    },
  },
  {
    headerName: "～",
    field: "SEPARATELY",
    type: CONTROL_TYPE.SEPARATELY,
    width: 30,
  },
  {
    headerName: "年",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 130,
    endGreaterStart: true,
    greaterThanField: "start_date",
    editInfo: {
      type: TYPE_INPUT.DATE_MONTH,
    },
  },
  {
    headerName: "期間",
    field: "period",
    type: CONTROL_TYPE.PERIOD,
    width: 90,
    field_start: "start_date",
    field_end: "end_date",
    unit: "ヶ月",
    typeTime: "month",
  },
  {
    headerName: "勤務先",
    field: "name",
    type: CONTROL_TYPE.INPUT,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "職務内容",
    field: "comment",
    type: CONTROL_TYPE.TEXT_AREA,
    minWidth: 170,
    editInfo: {
      type: TYPE_INPUT.TEXT_AREA,
    },
  },
  {
    headerName: "添付書類",
    field: "worker_attachments",
    type: CONTROL_TYPE.FILE,
    minWidth: 170,
  },
];

//tab3

export const employmentColumnDefs: (
  WORKER_TYPE_OPTIONS: any[],
) => ColDefCustom[] = (WORKER_TYPE_OPTIONS: any[]) => [
  {
    headerName: "契約種別",
    field: "working_type",
    type: CONTROL_TYPE.SELECT,
    width: 240,
    minWidth: 240,
    options: WORKER_TYPE_OPTIONS,
    isRequired: true,
    editInfo: {
      type: TYPE_INPUT.WORKER_TYPE_OPTIONS,
      rules: [
        {
          type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY,
        },
      ],
    },
  },
  {
    headerName: "スタッフ番号",
    field: "registration_number",
    type: CONTROL_TYPE.INPUT,
    width: 160,
    minWidth: 160,
    isRequired: true,
    editInfo: {
      type: TYPE_INPUT.TEXT,
      rules: [
        {
          type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY,
        },
      ],
    },
  },
  {
    headerName: "開始日（入社日）",
    field: "start_date",
    type: CONTROL_TYPE.DATE_V2,
    width: 190,
    timeRangeLock: true,
    endGreaterStart: true,
    lessThanField: "end_date",
    lockTimeBetweenRecords: true,
    minWidth: 160,
    isRequired: true,
    editInfo: {
      type: TYPE_INPUT.DATE,
      rules: [
        {
          type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY,
        },
      ],
    },
  },
  {
    headerName: "終了日（退社日、契約満了日）",
    field: "end_date",
    type: CONTROL_TYPE.DATE_V2,
    width: 160,
    greaterThanField: "start_date",
    timeRangeLock: true,
    endGreaterStart: true,
    disabledReCordPast: false,
    grayRecordPast: true,
    minWidth: 160,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "退社理由",
    field: "resignation_reason",
    type: CONTROL_TYPE.INPUT,
    width: 200,
    minWidth: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
    minWidth: 140,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const employmentRowSchema = Yup.object({
  registration_number: Yup.string().required(ERRORS_MESSAGE.REQUIRED),
  working_type: Yup.string().required(ERRORS_MESSAGE.REQUIRED),
  start_date: Yup.string().required(ERRORS_MESSAGE.REQUIRED),
});

/*---------------------------------------------------------
   2) 配置支店 / 配置部署
------------------------------------------------------------- */
export const assignmentColumnDefs: ColDef[] = [
  {
    headerName: "社会保険証番号",
    field: "branch",
    type: CONTROL_TYPE.INPUT,
    width: 200,
  },
  {
    headerName: "健康保険資格取得日",
    field: "department",
    type: CONTROL_TYPE.INPUT,
    width: 200,
  },
  {
    headerName: "健康保険資格喪失日",
    field: "department",
    type: CONTROL_TYPE.INPUT,
    width: 200,
  },
  {
    headerName: "システム日",
    field: "created_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
  },
];

export const assignmentRowSchema = Yup.object({
  branch: Yup.string().required("必須"),
  department: Yup.string().required("必須"),
  systemDate: Yup.string().required("必須"),
});

/*---------------------------------------------------------
   3) 業務体系号
------------------------------------------------------------- */
export const workSysColumnDefs: ColDefCustom[] = [
  {
    headerName: "業務端末電話番号",
    field: "device_number",
    type: CONTROL_TYPE.INPUT,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "備考",
    field: "note",
    type: CONTROL_TYPE.INPUT,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    width: 200,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    greaterThanField: "start_date",
    disabledReCordPast: false,
    endGreaterStart: true,
    grayRecordPast: true,
    width: 200,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const workSysRowSchema = Yup.object({});

export const documentsRowSchema = Yup.object({});

/*---------------------------------------------------------
   4) 社会保険 – 健康保険
------------------------------------------------------------- */
export const healthInsColumnDefs: ColDefCustom[] = [
  {
    headerName: "社会保険証番号",
    field: "insurance_number",
    type: CONTROL_TYPE.INPUT,
    width: 220,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "健康保険資格取得日",
    field: "insurance_get_date",
    startDateField: "insurance_get_date",
    lessThanField: "insurance_lost_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "健康保険資格喪失日",
    startDateField: "insurance_get_date",
    endDateField: "insurance_lost_date",
    field: "insurance_lost_date",
    greaterThanField: "insurance_get_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 200,
    timeRangeLock: true,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    greaterThanField: "start_date",
    disabledReCordPast: false,
    grayRecordPast: true,
    endGreaterStart: true,
    width: 200,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    minWidth: 180,
    disabled: true,
  },
];

export const healthInsRowSchema = Yup.object({});

/*---------------------------------------------------------
   5) 雇用保険 – 被保険者
------------------------------------------------------------- */
export const empInsColumnDefs: ColDefCustom[] = [
  {
    headerName: "雇用保険被保険者番号",
    field: "insurance_number",
    type: CONTROL_TYPE.INPUT,
    width: 220,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "雇用保険資格取得日",
    field: "insurance_get_date",
    startDateField: "insurance_get_date",
    lessThanField: "insurance_lost_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 180,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "雇用保険資格喪失日",
    field: "insurance_lost_date",
    startDateField: "insurance_get_date",
    endDateField: "insurance_lost_date",
    greaterThanField: "insurance_get_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 180,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    timeRangeLock: true,
    width: 180,
  },
  {
    headerName: "終了日",
    field: "end_date",
    greaterThanField: "start_date",
    disabledReCordPast: false,
    grayRecordPast: true,
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 180,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const empInsRowSchema = Yup.object({});

/*---------------------------------------------------------
   6) 標準報酬月額
------------------------------------------------------------- */
export const salaryColumnDefs: ColDefCustom[] = [
  {
    headerName: "保険適用都道府県",
    field: "prefecture_id",
    type: CONTROL_TYPE.PREFECTURE,
    width: 220,
    isRequired: true,
    editInfo: {
      type: TYPE_INPUT.PREFECTURE,
      rules: [{ type: RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY }],
    },
  },
  {
    headerName: "開始月",
    field: "start_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    picker: "month",
    width: 160,
    timeRangeLock: true,
    lessThanField: "end_date",
    lockTimeBetweenRecords: true,
  },
  {
    headerName: "終了月",
    field: "end_date",
    greaterThanField: "start_date",
    disabledReCordPast: false,
    type: CONTROL_TYPE.DATE,
    picker: "month",
    endGreaterStart: true,
    grayRecordPast: true,
    width: 160,
    lockTimeBetweenRecords: true,
    disabled: true,
  },
  {
    headerName: "移動日",
    field: "transition_date",
    type: CONTROL_TYPE.DATE,
    width: 160,
    editInfo: {
      type: TYPE_INPUT.DATE,
    },
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const salaryRows = [
  {
    id: uuidv4(),
    monthlySalary: 580000,
    prefecture: "aichi",
    startDate: "2024/05/02",
    endDate: "",
    systemDate: "2024/11/01",
    updatedBy: "愛知 花子",
  },
];

export const emailCompanyColumnDefs: ColDefCustom[] = [
  {
    headerName: "会社発行メールアドレス",
    field: "email",
    type: CONTROL_TYPE.INPUT,
    width: 220,
    editInfo: {
      type: TYPE_INPUT.EMAIL,
    },
  },
  {
    headerName: "パスワード",
    field: "password",
    type: CONTROL_TYPE.PASSWORD,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    lessThanField: "end_date",
    endGreaterStart: true,
    type: CONTROL_TYPE.DATE,
    width: 200,
  },
  {
    headerName: "終了日",
    field: "end_date",
    greaterThanField: "start_date",
    disabledReCordPast: false,
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    grayRecordPast: true,
    limitEndDateByNextStart: true,
    width: 200,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const emailCompanyRowSchema = Yup.object({
  email: Yup.string().email("必須").nullable(),
});

export const workerEmailsColumnDefs: ColDefCustom[] = [
  {
    headerName: "メールアドレス",
    field: "email",
    type: CONTROL_TYPE.INPUT,
    minWidth: 250,
    required: false,
    editInfo: {
      type: TYPE_INPUT.EMAIL,
    },
  },
  {
    headerName: "開始日",
    field: "start_date",
    type: CONTROL_TYPE.DATE,
    minWidth: 160,
    required: false,
    lessThanField: "end_date",
    endGreaterStart: true,
  },
  {
    headerName: "終了日",
    field: "end_date",
    type: CONTROL_TYPE.DATE,
    endGreaterStart: true,
    grayRecordPast: true,
    disabledReCordPast: false,
    minWidth: 160,
    disabled: false,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    minWidth: 160,
    disabled: true,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    minWidth: 160,
    disabled: true,
  },
];

export const documentsColumnDefs: ColDefCustom[] = [
  {
    headerName: "資料名",
    field: "document_name",
    type: CONTROL_TYPE.INPUT,
    width: 200,
    editInfo: {
      type: TYPE_INPUT.TEXT,
    },
  },
  {
    headerName: "添付書類",
    field: "document_files",
    type: CONTROL_TYPE.FILE,
    minWidth: 300,
  },
  {
    headerName: "システム日",
    field: "updated_at",
    type: CONTROL_TYPE.DATE,
    disabled: true,
    minWidth: 140,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    disabled: true,
    minWidth: 140,
  },
];

export const salaryRowSchema = Yup.object({
  // base_salary: Yup.number().min(0).required("必須"),
  prefecture_id: Yup.string().required("必須"),
  start_date: Yup.string().required("必須"),
  // end_date: Yup.string().nullable(),
});

//tab

export const companyInfoColumnDefs: ColDefCustom[] = [
  {
    headerName: "配属支店",
    field: "parent_org_node_name",
    type: CONTROL_TYPE.INPUT,
    width: 240,
  },
  {
    headerName: "配属部署",
    field: "position_name",
    type: CONTROL_TYPE.INPUT,
    width: 240,
  },
  {
    headerName: "適用日",
    field: "date_version",
    type: CONTROL_TYPE.DATE,
    width: 170,
  },
  {
    headerName: "システム日",
    field: "created_at",
    type: CONTROL_TYPE.DATE,
    width: 140,
  },
  {
    headerName: "更新者",
    field: "updated_by",
    type: CONTROL_TYPE.INPUT,
    minWidth: 240,
  },
];

export const companyInfoRowSchema = Yup.object({});

export const reduceData = (data: any, tableKey: string, apiData?: any[]) => {
  // Nếu có API data, sử dụng API data
  if (apiData && apiData.length > 0) {
    return apiData.map((item: any) => ({
      ...item,
      id: item.id || uuidv4(),
    }));
  }

  // Nếu không có API data, sử dụng mock data
  return data.map((item: any) => ({
    ...item,
    id: item.id || uuidv4(),
  }));
};

export const MAX_PHONE_LENGTH = 10;

export const userInfoTablesObj = {
  [TableKey.LOGIN_INFO]: {
    rowData: [],
    titleTable: "ログインID",
    columnDefs: columnLoginInfoDefs,
    key: TableKey.LOGIN_INFO,
    schema: Yup.object().shape({
      email: Yup.string()
        .required(ERRORS_MESSAGE.REQUIRED)
        .email(ERRORS_MESSAGE.EMAIL_INVALID),
      start_date: Yup.string().required(ERRORS_MESSAGE.REQUIRED),
    }),
  },
  [TableKey.NAME_HISTORY]: {
    rowData: [],
    titleTable: "氏名",
    columnDefs: nameHistoryColumnDefs,
    key: TableKey.NAME_HISTORY,
    schema: Yup.object().shape({
      lastname: Yup.string().required("名前は必須です"),
      firstname: Yup.string().required("名前カナは必須です"),
      start_date: Yup.string().required("_"),
    }),
  },
  [TableKey.GENDER]: {
    rowData: [],
    titleTable: "性別",
    columnDefs: genderColumnDefs,
    key: TableKey.GENDER,
    schema: Yup.object().shape({}),
  },

  [TableKey.CURRENT_ADDRESS]: {
    rowData: [],
    titleTable: "住所",
    columnDefs: addressNowColumnDefs,
    key: TableKey.CURRENT_ADDRESS,
    schema: Yup.object().shape({
      zip_code: Yup.string()
        .transform((value) => toHalfWidth(value.trim()) || null)
        .matches(REGEX.ZIP_CODE, ERRORS_MESSAGE.ZIPCODE)
        .nullable(),
    }),
  },
  // [TableKey.RESIDENT_ADDRESS]: {
  //   rowData: [],
  //   columnDefs: residentAddrColumnDefs,
  //   key: TableKey.RESIDENT_ADDRESS,
  //   schema: Yup.object().shape({
  //     start_date: Yup.string().required("_"),
  //     zip_code: Yup.string()
  //       .transform((value) => toHalfWidth(value.trim()) || null)
  //       .matches(REGEX.ZIP_CODE, "郵便番号が正しくありません")
  //       .nullable(),
  //   }),
  // },
  [TableKey.WORKER_PHONE_NUMBERS_LANDLINE]: {
    rowData: [],
    titleTable: "電話番号",
    columnDefs: phoneColumnDefs,
    key: TableKey.WORKER_PHONE_NUMBERS_LANDLINE,
    schema: Yup.object().shape({
      phone_number: Yup.string()
        .transform((value, originalValue) => {
          if (typeof originalValue === "string") {
            return convertCharterFullWidth(originalValue, true).trim();
          }
          return originalValue;
        })
        .nullable()
        .min(
          MAX_PHONE_LENGTH,
          `電話番号は${MAX_PHONE_LENGTH}桁以上で入力してください`,
        )
        .matches(
          REGEX.PHONE_NUMBER,
          "電話番号は数字、+、-、スペースのみ入力可能です",
        ),
    }),
  },
  [TableKey.WORKER_PHONE_NUMBERS_MOBILE]: {
    rowData: [],
    titleTable: "携帯番号",
    columnDefs: mobileColumnDefs,
    key: TableKey.WORKER_PHONE_NUMBERS_MOBILE,
    schema: Yup.object().shape({
      phone_number: Yup.string()
        .transform((value, originalValue) => {
          if (typeof originalValue === "string") {
            return convertCharterFullWidth(originalValue, true).trim();
          }
          return originalValue;
        })
        .nullable()
        .min(
          MAX_PHONE_LENGTH,
          `電話番号は${MAX_PHONE_LENGTH}桁以上で入力してください`,
        )
        .matches(
          REGEX.PHONE_NUMBER,
          "電話番号は数字、+、-、スペースのみ入力可能です",
        ),
    }),
  },
  [TableKey.WORKER_EMAILS]: {
    rowData: [],
    titleTable: "メールアドレス",
    columnDefs: workerEmailsColumnDefs,
    key: TableKey.WORKER_EMAILS,
    schema: Yup.object().shape({
      email: Yup.string()
        .email("有効なメールアドレスを入力してください")
        .nullable(),
    }),
  },
  [TableKey.WORKER_STORES]: {
    rowData: [],
    titleTable: "屋号",
    columnDefs: shopNameColumnDefs,
    key: TableKey.WORKER_STORES,
    schema: Yup.object().shape({}),
  },
  [TableKey.WORKER_BANK_INFOS]: {
    rowData: [],
    titleTable: "口座情報",
    note: "※振込予定日時点で有効な口座情報が給与明細に反映されます。",
    columnDefs: bankAccountColumnDefs,
    key: TableKey.WORKER_BANK_INFOS,
    schema: Yup.object().shape({}),
  },
  [TableKey.REGISTRATION_CODE]: {
    rowData: [],
    titleTable: "インボイス登録番号",
    columnDefs: registrationCodeTableColumnDefs,
    key: TableKey.REGISTRATION_CODE,
    schema: Yup.object().shape({
      registration_number: Yup.string()
        .transform((value, originalValue) => {
          if (typeof originalValue === "string") {
            return convertCharterFullWidth(originalValue, true).trim();
          }
          return originalValue;
        })
        .nullable(),
    }),
  },
};

const withCommonDatePicker = <T extends { columnDefs?: ColDefCustom[] }>(
  table: T,
): T => ({
  ...table,
  columnDefs: (table.columnDefs || []).map((col) =>
    col.type === CONTROL_TYPE.DATE
      ? {
          ...col,
          useCommonDatePicker: true,
        }
      : col,
  ),
});

export const createUserInfoTables = (
  tableData: Record<string | TableKey, any[] | undefined>,
) => [
  {
    type: "single-table",
    tables: [
      userInfoTablesObj[TableKey.NAME_HISTORY],
      userInfoTablesObj[TableKey.GENDER],
      userInfoTablesObj[TableKey.CURRENT_ADDRESS],
      // userInfoTablesObj[TableKey.RESIDENT_ADDRESS],
      userInfoTablesObj[TableKey.WORKER_PHONE_NUMBERS_LANDLINE],
      userInfoTablesObj[TableKey.WORKER_PHONE_NUMBERS_MOBILE],
      userInfoTablesObj[TableKey.WORKER_EMAILS],
      userInfoTablesObj[TableKey.WORKER_STORES],
      userInfoTablesObj[TableKey.REGISTRATION_CODE],
    ].map(withCommonDatePicker),
  },
  {
    type: "group-table",
    title: "緊急連絡先",
    tables: [
      withCommonDatePicker({
        rowData: [],
        columnDefs: emergencyBasicColumnDefs,
        key: TableKey.EMERGENCY_BASIC,
        titleTable: "",
        schema: Yup.object().shape({
          phone_number: Yup.string()
            .transform((value, originalValue) => {
              if (typeof originalValue === "string") {
                return convertCharterFullWidth(originalValue, true).trim();
              }
              return originalValue;
            })
            .nullable()
            .min(
              MAX_PHONE_LENGTH,
              `電話番号は${MAX_PHONE_LENGTH}桁以上で入力してください`,
            )
            .matches(
              REGEX.PHONE_NUMBER,
              "電話番号は数字、+、-、スペースのみ入力可能です",
            ),
          mobile_number: Yup.string()
            .transform((value, originalValue) => {
              if (typeof originalValue === "string") {
                return convertCharterFullWidth(originalValue, true).trim();
              }
              return originalValue;
            })
            .nullable()
            .min(
              MAX_PHONE_LENGTH,
              `電話番号は${MAX_PHONE_LENGTH}桁以上で入力してください`,
            )
            .matches(
              REGEX.PHONE_NUMBER,
              "電話番号は数字、+、-、スペースのみ入力可能です",
            ),
        }),
      }),
      withCommonDatePicker({
        rowData: [],
        columnDefs: emergencyAddrColumnDefs,
        titleTable: "",
        key: TableKey.EMERGENCY_ADDRESS,
        schema: Yup.object().shape({
          zip_code: Yup.string()
            .transform((value) => toHalfWidth(value.trim()) || null)
            .matches(REGEX.ZIP_CODE, ERRORS_MESSAGE.ZIPCODE)
            .nullable(),
        }),
      }),
    ],
  },
];

export const createLoginInfoTables = (
  tableData: Record<string | TableKey, any[] | undefined>,
) => [
  {
    type: "single-table",
    tables: [userInfoTablesObj[TableKey.LOGIN_INFO]].map(withCommonDatePicker),
  },
];

export const createEmploymentInfoTables = (
  tableData: Record<string, any[] | undefined>,
) => [
  {
    rowData: [],
    titleTable: "資格",
    columnDefs: qualificationsColumnDefs,
    key: TableKey.QUALIFICATIONS,
    schema: Yup.object().shape({}),
  },
  {
    rowData: [],
    titleTable: "学歴",
    columnDefs: educationWorkColumnDefs,
    key: TableKey.EDUCATION_WORK,
    schema: Yup.object().shape({}),
  },
  {
    rowData: [],
    titleTable: "職歴",
    columnDefs: jobDetailsColumnDefs,
    key: TableKey.JOB_DETAILS,
    schema: Yup.object().shape({}),
  },
];

// Function để tạo employmentJoiningInfomationTables với dynamic data
export const createJoiningInfoTables = (
  tableData: Record<string | TableKey, any[] | undefined>,
) => [
  {
    type: "single-table",
    tables: [userInfoTablesObj[TableKey.WORKER_BANK_INFOS]],
  },
];

// tab6 - dependentInfo - end

// Config cho các table trong tab 入社情報 (fallback với mock data)

//tab
/*---------------------------------------------
   入社情報  (join-info)  – các bảng con
------------------------------------------------- */
export const joinInfoTables = (WORKER_TYPE_OPTIONS: any[]) => [
  {
    titleTable: "契約種別・スタッフ番号",
    rowData: [],
    columnDefs: employmentColumnDefs(WORKER_TYPE_OPTIONS),
    key: TableKey.EMPLOYMENT, // join-info/employment
    schema: employmentRowSchema,
  },

  /* 2. 配置情報 */
  // {
  //   rowData: [],
  //   columnDefs: assignmentColumnDefs,
  //   key: TableKey.SOCIAL_SECURITY, // join-info/assignment
  //   schema: assignmentRowSchema,
  // },

  /* 4. 健康保険 */
  {
    titleTable: "社会保険",
    rowData: [],
    columnDefs: healthInsColumnDefs,
    key: TableKey.HEALTH_INSURANCE, // join-info/health-insurance
    schema: healthInsRowSchema,
  },

  /* 5. 雇用保険 */
  {
    titleTable: "雇用保険",
    rowData: [],
    columnDefs: empInsColumnDefs,
    key: TableKey.EMPLOYMENT_INSURANCE, // join-info/employment-insurance
    schema: empInsRowSchema,
  },

  /* 6. 標準報酬月額 */
  {
    titleTable: "保険適用都道府県",
    rowData: [],
    columnDefs: salaryColumnDefs,
    key: TableKey.WORKER_BASE_SALARIES, // join-info/salary-standard
    schema: salaryRowSchema,
    description:
      "※保険適用都道府県、開始日、終了日を変更する場合、社保の変更手続が必要です。",
  },
  {
    titleTable: "メールアドレス",
    rowData: [],
    columnDefs: emailCompanyColumnDefs,
    key: TableKey.WORKER_COMPANY_EMAIL, // join-info/email-company
    schema: emailCompanyRowSchema,
  },
  /* 6. 業務体系号 */
  {
    titleTable: "業務端末電話番号",
    rowData: [],
    columnDefs: workSysColumnDefs,
    key: TableKey.WORKER_DEVICES, // join-info/work-system
    schema: workSysRowSchema,
  },
  /* 7. 書類 */
  {
    titleTable: "資料",
    rowData: [],
    columnDefs: documentsColumnDefs,
    key: TableKey.WORKER_DOCUMENTS, // join-info/documents
    schema: documentsRowSchema,
  },
];

export const companyInfoTables = [
  {
    titleTable: "配属情報",
    rowData: [],
    columnDefs: companyInfoColumnDefs,
    key: TableKey.COMPANY_INFO, // join-info/employment
    schema: companyInfoRowSchema,
  },
];

//inject
// Lazy load all components
const componentMap: Record<
  TableKey,
  React.LazyExoticComponent<any> | undefined
> = {
  [TableKey.LOGIN_INFO]: lazy(
    () => import("./components/ProfileSection/LoginInfo"),
  ),
  [TableKey.NAME_HISTORY]: lazy(
    () => import("./components/ProfileSection/NameHistory"),
  ),
  [TableKey.GENDER]: lazy(() => import("./components/ProfileSection/Gender")),
  [TableKey.CURRENT_ADDRESS]: lazy(
    () => import("./components/ProfileSection/CurrentAddress"),
  ),
  [TableKey.RESIDENT_ADDRESS]: lazy(
    () => import("./components/ProfileSection/ResidentAddress"),
  ),
  [TableKey.WORKER_PHONE_NUMBERS_LANDLINE]: lazy(
    () => import("./components/ProfileSection/Phone"),
  ),
  [TableKey.WORKER_PHONE_NUMBERS_MOBILE]: lazy(
    () => import("./components/ProfileSection/Mobile"),
  ),
  [TableKey.WORKER_STORES]: lazy(
    () => import("./components/ProfileSection/ShopName"),
  ),
  [TableKey.WORKER_BANK_INFOS]: lazy(
    () => import("./components/ProfileSection/BankAccount"),
  ),
  [TableKey.EMERGENCY_BASIC]: lazy(
    () => import("./components/ProfileSection/EmergencyBasic"),
  ),
  [TableKey.EMERGENCY_ADDRESS]: lazy(
    () => import("./components/ProfileSection/EmergencyAddress"),
  ),
  [TableKey.WORKER_EMAILS]: lazy(
    () => import("./components/ProfileSection/WorkerEmails"),
  ),
  [TableKey.REGISTRATION_CODE]: lazy(
    () => import("./components/ProfileSection/RegistrationCodeTable"),
  ),
  //tab 2
  [TableKey.QUALIFICATIONS]: lazy(
    () => import("./components/Employment/Qualifications"),
  ),
  [TableKey.EDUCATION_WORK]: lazy(
    () => import("./components/Employment/EducationWork"),
  ),
  [TableKey.JOB_DETAILS]: lazy(
    () => import("./components/Employment/JobDetails"),
  ),
  //tab3
  [TableKey.EMPLOYMENT]: lazy(
    () => import("./components/WorkerInformation/Employment"),
  ),
  [TableKey.SOCIAL_SECURITY]: lazy(
    () => import("./components/WorkerInformation/SocialSecurity"),
  ),
  [TableKey.WORKER_DEVICES]: lazy(
    () => import("./components/WorkerInformation/WorkerDevices"),
  ),
  [TableKey.HEALTH_INSURANCE]: lazy(
    () => import("./components/WorkerInformation/HealthInsurance"),
  ),
  [TableKey.EMPLOYMENT_INSURANCE]: lazy(
    () => import("./components/WorkerInformation/EmploymentInsurance"),
  ),
  [TableKey.WORKER_BASE_SALARIES]: lazy(
    () => import("./components/WorkerInformation/SalaryInfo"),
  ),
  [TableKey.WORKER_COMPANY_EMAIL]: lazy(
    () => import("./components/WorkerInformation/EmailCompany"),
  ),
  [TableKey.WORKER_DOCUMENTS]: lazy(
    () => import("./components/WorkerInformation/Documents"),
  ),
  //Tab 社内情報
  [TableKey.COMPANY_INFO]: lazy(
    () => import("./components/CompanyInformation/CompanyInfo"),
  ),
  // tab6
  [TableKey.WORKER_DEPENDANT_RELATIONSHIPS]: lazy(
    () => import("./components/CompanyInformation/CompanyInfo"),
  ),
  [TableKey.WORKER_DEPENDANT_ADDRESSES]: lazy(
    () => import("./components/CompanyInformation/CompanyInfo"),
  ),
  [TableKey.WORKER_DEPENDANT_GENDERS]: lazy(
    () => import("./components/CompanyInformation/CompanyInfo"),
  ),
  [TableKey.WORKER_DEPENDANT_NAMES]: lazy(
    () => import("./components/CompanyInformation/CompanyInfo"),
  ),
};

// Loading component for tables
const TableLoading = () => <div style={{ textAlign: "center" }}></div>;

export const injectComponents = (key: TableKey, commonProps?: any) => {
  const Comp = componentMap[key];
  if (!Comp) return null;

  return (
    <Suspense fallback={<TableLoading />}>
      <Comp {...commonProps} tableName={key} />
    </Suspense>
  );
};

export enum TAB_KEYS {
  BASIC_INFORMATION = "basic_information",
  LOGIN_INFORMATION = "login_information",
  CAREER_QUALIFICATIONS = "career_qualifications",
  EMPLOYMENT_INFORMATION = "employment_information",
  BANK_ACCOUNT = "bank_account",
  DEPENDENT_INFORMATION = "dependent_information",
  INTERNAL_INFORMATION = "internal_information",
  AGREEMENTS = "agreements",
  EMPLOYEE_SHARE_INFORMATION = "employee_share_information",
  EMPLOYMENT_CONTRACTS = "employment_contracts",
  LABOR_DOCUMENTS = "labor_documents",
}

export const getAppTypeByTabs = (tabs: string[]) => {
  if (tabs.includes(TAB_KEYS.EMPLOYMENT_CONTRACTS)) {
    return APP_TYPES.EMPLOYMENT_CONTRACTS;
  }
  if (tabs.includes(TAB_KEYS.AGREEMENTS)) {
    return APP_TYPES.AGREEMENTS;
  }
  if (tabs.includes(TAB_KEYS.LABOR_DOCUMENTS)) {
    return APP_TYPES.LABOR_DOCUMENTS;
  }
};

const TAB_TARGET_BY_TAB_ID: Record<string, TAB_KEYS> = {
  tab1: TAB_KEYS.BASIC_INFORMATION,
  tab2: TAB_KEYS.LOGIN_INFORMATION,
  tab3: TAB_KEYS.CAREER_QUALIFICATIONS,
  tab4: TAB_KEYS.EMPLOYMENT_INFORMATION,
  tab5: TAB_KEYS.BANK_ACCOUNT,
  tab6: TAB_KEYS.DEPENDENT_INFORMATION,
  tab7: TAB_KEYS.INTERNAL_INFORMATION,
};

export const getParamsByTabs = (tabs: string[]) => {
  if (tabs.includes(TAB_KEYS.EMPLOYMENT_CONTRACTS)) {
    return {
      app_type: "employment_contract",
      include_latest_app_record: true,
    };
  }
  if (tabs.includes(TAB_KEYS.AGREEMENTS)) {
    return {
      app_type: "agreement",
      include_latest_app_record: true,
    };
  }
  if (tabs.includes(TAB_KEYS.LABOR_DOCUMENTS)) {
    return {
      app_type: APP_TYPES.LABOR_DOCUMENTS,
      include_latest_app_record: true,
    };
  }
  const primaryTab = tabs[0];

  return {
    target_tab: TAB_TARGET_BY_TAB_ID[primaryTab] || TAB_KEYS.LOGIN_INFORMATION,
  };
};

export const tabOrder = [
  TAB_KEYS.LOGIN_INFORMATION,
  TAB_KEYS.BASIC_INFORMATION,
  TAB_KEYS.CAREER_QUALIFICATIONS,
  TAB_KEYS.BANK_ACCOUNT,
  TAB_KEYS.DEPENDENT_INFORMATION,
  TAB_KEYS.EMPLOYMENT_INFORMATION,
  TAB_KEYS.INTERNAL_INFORMATION,
  TAB_KEYS.EMPLOYMENT_CONTRACTS,
  TAB_KEYS.EMPLOYEE_SHARE_INFORMATION,
  TAB_KEYS.AGREEMENTS,
];

export const tabShowScrollbar = [
  TAB_KEYS.LOGIN_INFORMATION,
  TAB_KEYS.BASIC_INFORMATION,
  TAB_KEYS.CAREER_QUALIFICATIONS,
  TAB_KEYS.BANK_ACCOUNT,
  TAB_KEYS.DEPENDENT_INFORMATION,
  TAB_KEYS.EMPLOYMENT_INFORMATION,
  TAB_KEYS.INTERNAL_INFORMATION,
  TAB_KEYS.EMPLOYEE_SHARE_INFORMATION,
];
